import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Download, Lock, Eye, EyeOff, ArrowLeft, Edit, Trash2, Key, Activity, FileText, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { EditWaitlistModal } from '@/components/modals/EditWaitlistModal';
import { DeleteConfirmDialog } from '@/components/modals/DeleteConfirmDialog';
import { ChangePasswordModal } from '@/components/modals/ChangePasswordModal';
import { WestHamRelegationModal } from '@/components/modals/WestHamRelegationModal';
import { authenticate, getAdminSession, setAdminSession, clearAdminSession } from '@/lib/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [waitlistData, setWaitlistData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, byRole: {} });
  const navigate = useNavigate();
  
  // Modal states
  const [editEntry, setEditEntry] = useState(null);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showWestHamModal, setShowWestHamModal] = useState(false);

  // Restore session from sessionStorage on mount (so back/refresh keeps you logged in)
  useEffect(() => {
    const session = getAdminSession();
    if (session) {
      setCurrentUser(session);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWaitlistData();
    }
  }, [isAuthenticated]);
  
  const handleLogin = (e) => {
    e.preventDefault();

    const user = authenticate(email, password);
    if (user) {
      setAdminSession(user);
      setCurrentUser(user);
      setIsAuthenticated(true);

      // Log admin activity
      axios.post(`${API}/admin/activity`, {
        email: user.email,
        name: user.name,
        role: user.role,
      }).catch(err => console.error('Failed to log activity:', err));

      toast.success(`Welcome back, ${user.name}!`);

      // Easter egg: cheeky popup just for Chris. Shows max 2 times ever (persisted in localStorage).
      if (user.email === 'chris@ref-assist.com') {
        const count = parseInt(localStorage.getItem('refassist_chris_easter_egg_count') || '0', 10);
        if (count < 2) {
          localStorage.setItem('refassist_chris_easter_egg_count', String(count + 1));
          setTimeout(() => setShowWestHamModal(true), 600);
        }
      }
    } else {
      toast.error('Invalid email or password');
    }
  };
  
  const handleLogout = () => {
    clearAdminSession();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setEmail('');
    setPassword('');
    toast.success('Logged out successfully');
  };
  
  const fetchWaitlistData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/waitlist`);
      setWaitlistData(response.data);
      
      const byRole = {};
      response.data.forEach(item => {
        byRole[item.role] = (byRole[item.role] || 0) + 1;
      });
      setStats({ total: response.data.length, byRole });
    } catch (error) {
      toast.error('Failed to fetch waitlist data');
      console.error('Fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDownloadCSV = async () => {
    try {
      const response = await axios.get(`${API}/waitlist/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ref-assist-waitlist-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV downloaded successfully');
    } catch (error) {
      toast.error('Failed to download CSV');
      console.error('Download error:', error);
    }
  };
  
  const handleEditSave = async (formData) => {
    try {
      await axios.put(`${API}/waitlist/${editEntry.email}`, formData);
      
      // Log audit
      await axios.post(`${API}/admin/audit`, {
        admin_email: currentUser.email,
        admin_name: currentUser.name,
        action: 'edit',
        target: 'waitlist',
        entry_id: editEntry.email,
        changes: `Updated waitlist entry for ${formData.email}`
      });
      
      toast.success('Entry updated successfully');
      setEditEntry(null);
      fetchWaitlistData();
    } catch (error) {
      toast.error('Failed to update entry');
      console.error('Update error:', error);
    }
  };
  
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/waitlist/${deleteEntry.email}`);
      
      // Log audit
      await axios.post(`${API}/admin/audit`, {
        admin_email: currentUser.email,
        admin_name: currentUser.name,
        action: 'delete',
        target: 'waitlist',
        entry_id: deleteEntry.email,
        changes: `Deleted waitlist entry for ${deleteEntry.email}`
      });
      
      toast.success('Entry deleted successfully');
      setDeleteEntry(null);
      fetchWaitlistData();
    } catch (error) {
      toast.error('Failed to delete entry');
      console.error('Delete error:', error);
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handlePasswordChange = async (formData) => {
    // For now, show message that password change requires code update
    toast.info('Password changes require updating the codebase. Contact system administrator.');
    return false;
  };
  
  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border/50 p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2 font-['Space_Grotesk']">Admin Access</h1>
            <p className="text-sm text-muted-foreground">Sign in to manage waitlist</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@ref-assist.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-muted/50 border-border/50 focus:border-primary text-foreground h-11"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-muted/50 border-border/50 focus:border-primary text-foreground h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-11">
              Sign In
            </Button>
            
            <Button 
              type="button" 
              variant="ghost" 
              className="w-full"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </form>
        </Card>
      </div>
    );
  }
  
  const isSuperAdmin = currentUser.role === 'superadmin';
  
  // Admin Dashboard
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground font-['Space_Grotesk']">Waitlist Dashboard</h1>
              {currentUser && (
                <Badge className={currentUser.role === 'superadmin' ? 'bg-accent/20 text-accent border-accent/30' : 'bg-primary/20 text-primary border-primary/30'}>
                  {currentUser.role === 'superadmin' ? 'Super Admin' : 'Admin'}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">
              Welcome back, <span className="font-medium text-foreground">{currentUser?.name}</span> ({currentUser?.email})
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isSuperAdmin && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/admin/activity', { state: { currentUser } })}
                  className="border-primary/30"
                >
                  <Activity className="w-4 h-4 mr-2" />
                  Activity Log
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/admin/audit', { state: { currentUser } })}
                  className="border-secondary/30"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Audit Log
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/admin/investor-pack', { state: { currentUser } })}
                  className="border-accent/30"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Investor Pack
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/deleted', { state: { currentUser } })}
                  className="border-destructive/40 text-destructive hover:bg-destructive/10"
                  data-testid="recently-deleted-btn"
                >
                  <Undo2 className="w-4 h-4 mr-2" />
                  Recently Deleted
                </Button>
              </>
            )}
            <Button 
              variant="outline"
              onClick={() => setShowPasswordModal(true)}
            >
              <Key className="w-4 h-4 mr-2" />
              Change Password
            </Button>
            <Button 
              onClick={handleDownloadCSV}
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin/pilot')}
              className="border-accent/30 text-accent hover:bg-accent/10"
            >
              Pilot Apps
            </Button>
            <Button 
              variant="outline" 
              onClick={handleLogout}
            >
              Logout
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Home
            </Button>
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-card border-border/50 p-6">
            <div className="text-sm text-muted-foreground mb-1">Total Signups</div>
            <div className="text-3xl font-bold text-foreground">{stats.total}</div>
          </Card>
          
          {Object.entries(stats.byRole).slice(0, 3).map(([role, count]) => (
            <Card key={role} className="bg-card border-border/50 p-6">
              <div className="text-sm text-muted-foreground mb-1 capitalize">{role}s</div>
              <div className="text-3xl font-bold text-foreground">{count}</div>
            </Card>
          ))}
        </div>
        
        {/* Waitlist Table */}
        <Card className="bg-card border-border/50 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50">
                  <TableHead className="text-foreground">Email</TableHead>
                  <TableHead className="text-foreground">Name</TableHead>
                  <TableHead className="text-foreground">Role</TableHead>
                  <TableHead className="text-foreground">Country</TableHead>
                  <TableHead className="text-foreground">Message</TableHead>
                  <TableHead className="text-foreground">Joined</TableHead>
                  {isSuperAdmin && <TableHead className="text-foreground">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 7 : 6} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : waitlistData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 7 : 6} className="text-center py-8 text-muted-foreground">
                      No signups yet
                    </TableCell>
                  </TableRow>
                ) : (
                  waitlistData.map((item, index) => (
                    <TableRow key={index} className="border-border/50">
                      <TableCell className="font-medium text-foreground">{item.email}</TableCell>
                      <TableCell className="text-foreground">{item.name || '-'}</TableCell>
                      <TableCell>
                        <Badge className="bg-primary/10 text-primary border-primary/20 capitalize">
                          {item.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-foreground">{item.country || '-'}</TableCell>
                      <TableCell className="text-foreground max-w-xs truncate" title={item.message}>
                        {item.message || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(item.created_at).toLocaleDateString()}
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditEntry(item)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteEntry(item)}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
      
      {/* Modals */}
      <EditWaitlistModal
        entry={editEntry}
        open={!!editEntry}
        onClose={() => setEditEntry(null)}
        onSave={handleEditSave}
      />
      
      <DeleteConfirmDialog
        entry={deleteEntry}
        type="waitlist"
        open={!!deleteEntry}
        onClose={() => setDeleteEntry(null)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
      
      <ChangePasswordModal
        currentUser={currentUser}
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSave={handlePasswordChange}
      />

      <WestHamRelegationModal
        open={showWestHamModal}
        onClose={() => setShowWestHamModal(false)}
      />
    </div>
  );
}
